import React, { useState, useRef, useEffect, useCallback } from 'react';
import { TouchpadInput } from '../../ui/TouchpadInput';

interface AnswerInputProps {
  /** Expected answer — used for auto-submit when input length matches */
  answer: number;
  /** Called when the student submits an answer */
  onSubmit: (value: number) => void;
  /** Disables input (e.g. while advancing to next fact) */
  disabled?: boolean;
  /** Feedback state — controls input border color */
  feedback?: 'correct' | 'incorrect' | null;
  /** When this value changes, the input clears and re-focuses (e.g. pass factIndex or factKey) */
  resetKey?: string | number;
}

const AnswerInput: React.FC<AnswerInputProps> = ({
  answer,
  onSubmit,
  disabled = false,
  feedback = null,
  resetKey = 0,
}) => {
  const [value, setValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const showFeedback = feedback !== null;

  // Clear and re-focus when resetKey changes
  useEffect(() => {
    setValue('');
  }, [resetKey]);

  // Clear when feedback clears
  const prevFeedbackRef = useRef(feedback);
  useEffect(() => {
    if (prevFeedbackRef.current !== null && feedback === null) {
      setValue('');
    }
    prevFeedbackRef.current = feedback;
  }, [feedback]);

  // Auto-focus when enabled
  useEffect(() => {
    if (!showFeedback && !disabled && inputRef.current) {
      inputRef.current.focus();
    }
  }, [showFeedback, disabled, resetKey]);

  const handleInputChange = useCallback(
    (newValue: string) => {
      if (showFeedback || disabled) return;
      setValue(newValue);

      // Auto-submit when input length matches answer length
      if (newValue.length === answer.toString().length) {
        const numValue = parseInt(newValue);
        if (!isNaN(numValue)) {
          onSubmit(numValue);
        }
      }
    },
    [showFeedback, disabled, answer, onSubmit]
  );

  const handleNativeInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value.replace(/\D/g, '');
    handleInputChange(newValue);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && value) {
      const numValue = parseInt(value);
      if (!isNaN(numValue)) {
        onSubmit(numValue);
      }
    }
  };

  return (
    <div className="flex justify-center gap-4">
      <div className="flex flex-col items-center justify-center">
        <input
          ref={inputRef}
          type="text"
          inputMode="numeric"
          pattern="\d*"
          value={value}
          onChange={handleNativeInputChange}
          onKeyDown={handleKeyPress}
          disabled={showFeedback || disabled}
          className={`
            w-48 p-4 text-4xl font-bold text-center rounded-lg border-4 outline-none
            transition-all duration-200
            ${!showFeedback
              ? 'border-gray-200 focus:border-gray-200 bg-white text-gray-700'
              : feedback === 'correct'
                ? 'border-green-500 bg-green-50 text-green-700'
                : 'border-red-500 bg-red-50 text-red-700'
            }
          `}
        />
      </div>

      <TouchpadInput
        value={value}
        onChange={handleInputChange}
        disabled={showFeedback || disabled}
        showFeedback={showFeedback}
      />
    </div>
  );
};

export default AnswerInput;
